import { useEffect, type MutableRefObject } from 'react';

import { hasValue } from 'src/packages/utils/has-value';

import type { DirectoryTableMediator } from '../directory-table-mediator';
import type {
  GridApi,
  GridCellEditStartParams,
  GridCellEditStopParams,
  GridCellParams,
  MuiBaseEvent,
  MuiEvent,
} from '@repo/go-table';
import type { MainDirectoryService } from 'src/services/directory-service';
import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { InterTablesInteractions } from 'src/services/inter-tables-interactions';

interface Props {
  tableApi: MutableRefObject<GridApi>;
  store: DirectoryTableMediator;
  directory: MainDirectoryService;
  interTableInteractions: InterTablesInteractions;
}

export const useTableEventHandlers = ({ tableApi, store, directory, interTableInteractions }: Props) => {
  useEffect(() => {
    let timer: number | undefined;

    const handleCopy = (event: KeyboardEvent) => {
      if (event.code !== 'KeyC' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      if (hasValue(timer)) {
        return;
      }

      const selectedRows = tableApi.current.getSelectedRows();

      if (selectedRows.size < 1) {
        const cellSelection = tableApi.current.getCellSelectionModel();

        const tableCopyTargetData = (() => {
          if (cellSelection && !!Object.keys(cellSelection).length) {
            return cellSelection;
          }

          if (directory.tableSettingsManager?.selectedCellData) {
            return {
              [directory.tableSettingsManager.selectedCellData.rowId]: {
                [directory.tableSettingsManager.selectedCellData.cellField]: true,
              },
            };
          }

          return null;
        })();

        if (!tableCopyTargetData) {
          return;
        }

        directory.directoryViewMediator?.copySelectedCellData(tableCopyTargetData);

        timer = window.setTimeout(() => {
          timer = undefined;
        }, 500);

        return;
      }

      if (interTableInteractions.activeTable === directory.entityId) {
        directory.directoryViewMediator?.copySelectedRowsToClipboard();
      }

      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handleCopy);

    return () => {
      document.removeEventListener('keydown', handleCopy);
      window.clearTimeout(timer);
    };
  }, [
    directory.directoryViewMediator,
    directory.entityId,
    directory.tableSettingsManager.selectedCellData,
    interTableInteractions.activeTable,
    tableApi,
  ]);

  useEffect(() => {
    let timer: number | undefined;

    const handlePaste = (event: KeyboardEvent) => {
      if (event.code !== 'KeyV' || (!event.ctrlKey && !event.metaKey)) {
        return;
      }

      if (interTableInteractions.activeTable !== directory.entityId) {
        return;
      }

      // игнорируем, если событие реагирует на вставку текста в input поле т.к. в таком случае должна отработать дефолтная логика вставки браузера
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      if (hasValue(timer)) {
        return;
      }

      event.preventDefault();

      directory.directoryViewMediator?.pasteData(directory.tableSettingsManager?.selectedCellData || undefined);
      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    };

    document.addEventListener('keydown', handlePaste);

    return () => {
      document.removeEventListener('keydown', handlePaste);
      window.clearTimeout(timer);
    };
  }, [
    directory.directoryViewMediator,
    directory.entityId,
    directory.tableSettingsManager?.selectedCellData,
    interTableInteractions,
  ]);

  useEffect(() => {
    const handleFocusIn = ({ field, row }: GridCellParams) => {
      const focusedCellData = { cellField: field, rowId: row.recordId };
      directory.tableSettingsManager?.setSelectedCellData(focusedCellData);
    };

    return tableApi.current.subscribeEvent('cellFocusIn', handleFocusIn);
  }, [directory.tableSettingsManager, tableApi]);

  useEffect(() => {
    return tableApi.current.subscribeEvent('cellMouseDown', () => {
      interTableInteractions.setActiveTable(directory.entityId);
    });
  }, [directory.entityId, interTableInteractions, tableApi]);

  const handleCellDeletePress = (params: GridCellEditStartParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === 'deleteKeyDown') {
      const cellSelectionModel = tableApi.current.getCellSelectionModel();

      const removeValue = (fieldId: string, recordId: number) => {
        directory.directoryViewMediator?.onControlValueChange(recordId, fieldId, null);
      };

      const rowIds = Object.keys(cellSelectionModel);

      if (!rowIds.length) {
        return;
      }

      if (rowIds.length > 1 || Object.keys(cellSelectionModel[rowIds[0]]).length) {
        event.defaultMuiPrevented = true;
        const rows: DirectoryRecord[] = [];

        for (const recordId in cellSelectionModel) {
          if (Number.isNaN(Number(recordId))) {
            console.error('record id is not valid');
            continue;
          }

          const row = tableApi.current.getRow(Number(recordId));

          if (!row) {
            continue;
          }

          rows.push(row);

          for (const fieldId in cellSelectionModel[recordId]) {
            removeValue(fieldId, Number(recordId));
          }
        }

        tableApi.current.updateRows(rows);
      } else {
        removeValue(params.colDef.field, params.row.recordId);
      }
    }
  };

  const handleComboboxEnterPress = (params: GridCellEditStopParams, event: MuiEvent<MuiBaseEvent>) => {
    if (params.reason === 'enterKeyDown') {
      const fieldView = directory.controlsViewsMap.get(params.colDef.field);

      if (!fieldView) {
        console.error('cannot find corresponding fieldView');
        return;
      }

      if (fieldView.control === 'MultiComboBox') {
        event.defaultMuiPrevented = true;
      }
    }
  };

  return {
    handleCellDeletePress,
    handleComboboxEnterPress,
  };
};
